/**
 * Bahadura & Besa Branch Feature Flags
 */
export const branchFeatureFlags = {
  ENABLE_TWO_STAGE_WORKFLOW: true,
  ENABLE_L2_DEMAND_NOTE_GENERATION: true,
  ENABLE_TEMPORARY_RL_CERTIFICATE: true,
  ENABLE_PHASE2_PERMANENT_RL_UPLOAD: true,
  // Feature flag to control whether Level 2 shows the "Upload Map After Digital Signature" screen.
  // Set to false to bypass UploadMapWrapper and navigate directly to /level-2-applications upon forwarding.
  ENABLE_L2_UPLOAD_MAP_SCREEN: false,
  /**
   * Set to true to offer the third Application Type, "Offline Tentative" (the
   * applicant already holds a Tentative RL certificate and Demand Note issued
   * on paper). When false, the option is left out of the Application Type
   * dropdown. The backend has the same flag in api/config/branchFeatureFlags.js;
   * keep the two in step.
   */
  ENABLE_OFFLINE_TENTATIVE_RL: true,
  /**
   * Set to true to show reviewers the "Edit" button on the application
   * details screen, which lets them correct an application's form fields in
   * place of returning it to the applicant. When false, the button is not
   * rendered. The backend has the same flag in
   * api/config/branchFeatureFlags.js; keep the two in step.
   */
  ENABLE_REVIEWER_APPLICATION_EDIT: true,
  /**
   * How the Level 3 officer signs the Demand Note and the RL Certificate:
   *   "DIGITAL_ONLY" - only the Apply DSC button (USB token); manual upload hidden.
   *   "BOTH"         - Apply DSC and the manual download / sign / upload.
   *   "MANUAL_ONLY"  - only the manual upload, as it was before Apply DSC existed.
   * Apply DSC needs the signer program running on the officer's computer; see
   * config/levelThreeSigning.ts.
   */
  L3_SIGNING_MODE: "BOTH",
  /**
   * Whether this municipality has the Application Access feature: the Admin
   * bars a user, or a whole user type, from starting a new application or
   * submitting one. When false the Admin menu entry is hidden and no
   * applicant is treated as blocked. The backend has the same flag in
   * api/config/branchFeatureFlags.js; keep the two in step.
   */
  ENABLE_APPLICATION_ACCESS_CONTROL: true,
  /**
   * Whether this municipality has the Sidebar Menu Config feature: the Admin
   * switches the Dashboard, Rejected Applications and Reports sidebar entries
   * on or off for Level 1, Level 2 and Level 3. When false the Admin menu
   * entry is hidden and every reviewer is shown the full sidebar. The backend
   * has the same flag in api/config/branchFeatureFlags.js; keep the two in
   * step.
   */
  ENABLE_SIDEBAR_MENU_CONFIG: true,
  /**
   * Whether this municipality has the Payment Installments feature: an
   * applicant pays a Demand Note in up to three receipts, and the Admin
   * switches that on or off. When false the Admin menu entry is hidden and
   * every payment follows the one-receipt behaviour. The backend has the same
   * flag in api/config/branchFeatureFlags.js; keep the two in step.
   */
  ENABLE_PAYMENT_INSTALLMENTS: true,
  /**
   * Whether this municipality has the Field Sequence feature: the Admin sets
   * the order in which the applicant form lists its fields, and switches
   * between that order and the default one. When false the Admin menu entry
   * is hidden and the form keeps its default order. The backend has the same
   * flag in api/config/branchFeatureFlags.js; keep the two in step.
   */
  ENABLE_FIELD_SEQUENCE: true,
  /**
   * Whether the Field Sequence screen lists the NOC step. The NOC form is not
   * in use, and the applicant form draws that step with its own layout
   * (RenderNOC), so it is left out of the screen. Set to true to list it
   * again, locked.
   */
  FIELD_SEQUENCE_SHOW_NOC_STEP: false,
  /**
   * Whether the Field Sequence screen lists the Documents step and the
   * documents of any step. Documents keep their own order
   * (DOCUMENT_DISPLAY_ORDER) and are not ordered on this screen, so they are
   * left out. Set to true to list them again, locked, for reference.
   */
  FIELD_SEQUENCE_SHOW_DOCUMENTS_STEP: false,
  /**
   * Whether this municipality has the Schematic Plan file upload: on the
   * Schematic Plan step Level 1 and Level 2 choose between drawing the
   * schematic and uploading one PDF/JPG/PNG (up to 10 MB), Level 3 edits what
   * Level 2 forwarded, and the certificate carries the file's pages between
   * page 1 and the conditions page. The application details screens show the
   * file. When false the step is the drawing only, as before. The backend has
   * the same flag in api/config/branchFeatureFlags.js; keep the two in step.
   */
  ENABLE_SCHEMATIC_FILE_UPLOAD: true,
  /**
   * Whether an Offline Tentative application gets two more Schematic Plan
   * choices: "Use applicant's schematic" (the applicant's optional Schematic
   * Plan document goes into the certificate) and "No schematic plan" (the
   * certificate has no schematic). Needs ENABLE_SCHEMATIC_FILE_UPLOAD. When
   * false Offline Tentative keeps Draw / Upload only. The backend has the same
   * flag in api/config/branchFeatureFlags.js; keep the two in step.
   */
  ENABLE_OFFLINE_SCHEMATIC_OPTIONS: true,
  /**
   * Whether the Schematic Plan step offers "Upload image + dimensions" to
   * Level 1, 2 and 3: one JPG/PNG of the plot, the four sides (AB, BC, CD,
   * DA) and one direction, shown inside the certificate's SCHEMATIC PLAN box
   * the same way as Gondia. Offline Tentative gets it too. Needs
   * ENABLE_SCHEMATIC_FILE_UPLOAD. The backend has the same flag in
   * api/config/branchFeatureFlags.js; keep the two in step.
   */
  ENABLE_SCHEMATIC_IMAGE_DIMENSIONS: true,
  /**
   * Whether the L1 / L2 / L3 application lists open showing only the
   * applications waiting for that level (System Admin with Admin Mode off
   * uses the L3 list), with a "Show All Applications" button to switch to the
   * full list and "Show Pending Applications" to switch back. When false the
   * lists show every application and the button is hidden, as before. The
   * backend has the same flag in api/config/branchFeatureFlags.js; keep the
   * two in step.
   */
  ENABLE_LEVEL_PENDING_VIEW: true,
  /**
   * Whether "Upload image + dimensions" is the only Schematic Plan choice.
   * When true the step shows no choice cards: every application other than
   * Offline Tentative opens straight on the image + dimensions panel, and
   * Offline Tentative opens on "No schematic plan" (its certificate has no
   * schematic). An application already forwarded to Level 3 with a drawing
   * or a file keeps it. Needs ENABLE_SCHEMATIC_IMAGE_DIMENSIONS. The backend
   * has the same flag in api/config/branchFeatureFlags.js; keep the two in
   * step.
   */
  ENABLE_SCHEMATIC_IMAGE_ONLY: true,
};
